/**
 * Firestore payload sanitization — fixes the infamous 400 "Unsupported field value: undefined".
 *
 * Firestore strictly rejects documents containing `undefined` values, functions or
 * circular references. Dumping a raw Zustand state (actions included) therefore
 * crashes the write with HTTP 400. Every cloud save funnels through the helpers
 * below so only plain, JSON-safe data models ever reach Firestore.
 */

/**
 * Pure JSON-safe serializer:
 *  - `undefined` → `null` (Firestore cannot store undefined),
 *  - functions (store actions) → dropped entirely,
 *  - circular references → pruned by the seen-set fallback.
 */
export const sanitizeForFirestore = (obj: unknown): any =>
  JSON.parse(
    JSON.stringify(obj, (_key, value: unknown) => {
      if (value === undefined) return null;
      if (typeof value === 'function') return undefined; // omit key entirely
      return value;
    }),
  );

/**
 * Safe slot document id: never empty, never containing spaces, slashes or dots.
 * `Ulice 12 / hlavní` → `Ulice_12___hlavn_`.
 */
export const safeSlotId = (slotName: string): string =>
  slotName.trim().replace(/[^a-zA-Z0-9_-]/g, '_') || 'save_slot_1';

/**
 * Strict state extraction — save ONLY the data models the game needs to resume.
 * Anything else (transient UI gates, toasts, animations, functions) is dropped.
 */
export const buildCloudPayload = (snapshot: Record<string, unknown>): Record<string, unknown> =>
  sanitizeForFirestore({
    schema: snapshot.schema,
    version: snapshot.version,
    savedAt: snapshot.savedAt,
    gameStarted: snapshot.gameStarted,
    lobby: snapshot.lobby,
    victory: snapshot.victory,
    board: snapshot.board,
    players: snapshot.players,
    market: snapshot.market,
    currentPlayerIndex: snapshot.currentPlayerIndex,
    turnPhase: snapshot.turnPhase,
    events: snapshot.events,
    loans: snapshot.loans,
    investments: snapshot.investments,
    auction: snapshot.auction,
    pendingOffer: snapshot.pendingOffer,
    proposal: snapshot.proposal,
    gameOver: snapshot.gameOver,
    winnerId: snapshot.winnerId,
    lastTakeoverRound: snapshot.lastTakeoverRound,
    expansionPending: snapshot.expansionPending,
    metroRiddenThisTurn: snapshot.metroRiddenThisTurn,
    selectedTileIndex: snapshot.selectedTileIndex,
    movementPoints: snapshot.movementPoints,
    reachableIndices: snapshot.reachableIndices,
  });
